import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatDecimalForInput,
  parseDecimalInput,
} from "../../../../../../common/input/parseDecimalInput";
import { useDebouncedSave } from "../../../../../../common/input/useDebouncedSave";
import { useContractState } from "../../../useContractState";
import { RemoveDataFieldButton } from "../RemoveDataFieldButton";

interface Props {
  label: string;
  placeholderText: string;
  amount: number | null;
  errorMessage: string | null;
  editAmount: (amount: number | undefined) => Promise<void> | void;
  helperText: string;
  removeDataField?: () => Promise<void> | void;
  isRequiredButNotProvided: boolean;
  wholeAmountsOnly?: boolean;
}

export const GenericMonetaryValue = ({
  label,
  placeholderText,
  amount,
  errorMessage,
  editAmount,
  helperText,
  removeDataField,
  isRequiredButNotProvided,
  wholeAmountsOnly = false,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const currency = useContractState().contract.currency;
  const [typedAmount, setTypedAmount] = useState<string | undefined>(undefined);
  const amountBeforeEdit = useRef<number | undefined | null>(null);
  const lastSavedAmount = useRef<number | undefined>(amount ?? undefined);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const parsedTypedAmount =
    typedAmount === undefined
      ? undefined
      : parseDecimalInput(typedAmount, { allowSpaceGrouping: true });
  const isTypedAmountNotANumber = parsedTypedAmount?.kind === "notANumber";
  const isTypedAmountNotWhole =
    wholeAmountsOnly &&
    parsedTypedAmount?.kind === "number" &&
    !Number.isInteger(parsedTypedAmount.value);

  useEffect(() => {
    if (amount !== undefined && amount !== null && typedAmount === undefined)
      setTypedAmount(formatDecimalForInput(amount));
  }, [amount, typedAmount]);

  const debouncedAmountEventHandler = useDebouncedSave((value: number | undefined) => {
    lastSavedAmount.current = value;
    saveChain.current = saveChain.current
      .then(async () => {
        setIsLoading(true);
        await editAmount(value);
      })
      .catch(() => undefined)
      .finally(() => setIsLoading(false));
    return saveChain.current;
  });

  return (
    <FormControl
      isInvalid={
        errorMessage !== null ||
        isTypedAmountNotANumber ||
        isTypedAmountNotWhole
      }
      isRequired={isRequiredButNotProvided}
    >
      <FormLabel htmlFor={"value"}>
        {label} {isLoading && <Spinner size="xs" />}
      </FormLabel>
      <Box borderRadius={"10px"}>
        <Flex w="100%">
          <InputGroup>
            <InputLeftAddon>{currency}</InputLeftAddon>
            <Input
              id="value"
              bg="smBackground"
              w="100%"
              borderLeftRadius="0"
              inputMode="decimal"
              autoComplete="off"
              value={typedAmount ?? ""}
              placeholder={placeholderText}
              onFocus={() => {
                amountBeforeEdit.current = amount ?? undefined;
                lastSavedAmount.current = amount ?? undefined;
              }}
              onChange={(e) => {
                setTypedAmount(e.target.value);
                const parsed = parseDecimalInput(e.target.value, {
                  allowSpaceGrouping: true,
                });
                if (
                  parsed.kind === "notANumber" ||
                  (wholeAmountsOnly &&
                    parsed.kind === "number" &&
                    !Number.isInteger(parsed.value))
                ) {
                  debouncedAmountEventHandler.cancel();
                  const beforeEdit = amountBeforeEdit.current;
                  if (beforeEdit !== null && lastSavedAmount.current !== beforeEdit) {
                    debouncedAmountEventHandler(beforeEdit);
                  }
                  return;
                }
                debouncedAmountEventHandler(
                  parsed.kind === "number" ? parsed.value : undefined,
                );
              }}
            />
          </InputGroup>
          {removeDataField && (
            <RemoveDataFieldButton removeDataField={removeDataField} />
          )}
        </Flex>
      </Box>
      <FormHelperText>{helperText}</FormHelperText>
      <FormErrorMessage>
        {isTypedAmountNotANumber && wholeAmountsOnly
          ? t("Write the amount as a number, for example 1 234")
          : isTypedAmountNotANumber
          ? t("Write the amount as a number, for example 1 234,50")
          : isTypedAmountNotWhole
            ? t("Write the amount without decimals, for example 1 234")
            : errorMessage}
      </FormErrorMessage>
    </FormControl>
  );
};
